import { useMsal } from "@azure/msal-react";
import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import backlogIcon from "../../assets/backlog.svg";
import cancelledIcon from "../../assets/cancelled.svg";
import doneIcon from "../../assets/done.svg";
import extendedFeaturesIcon from "../../assets/extended-features.svg";
import inProgressIcon from "../../assets/in-progress.svg";
import inReviewIcon from "../../assets/in-review.svg";
import paperClipIcon from "../../assets/paperclip.svg";
import todoIcon from "../../assets/todo.svg";
import triageIcon from "../../assets/triage.svg";
import {
	createIssue,
	getBoard,
	LinearIssueSummary,
	LinearState,
} from "../api/linearClient";
import InternalModal from "../components/internal-modal/InternalModal.comp";
import { useInternalHeader } from "../InternalHeaderContext";
import { internalApiScopes } from "../msalConfig";
import styles from "../styles/board.module.scss";

function normalizeKey(value: string) {
	return value.trim().toLowerCase().replace(/\s+/g, " ");
}

enum BoardColumnKey {
	Triage = "triage",
	Backlog = "backlog",
	Todo = "todo",
	ToDo = "to do",
	InProgress = "in progress",
	InReview = "in review",
	Done = "done",
	Cancelled = "cancelled",
	Canceled = "canceled",
	Duplicate = "duplicate",
	ExtendedFeatures = "extended features",
	PaperClip = "paperclip",
}

function getStateKey(state: LinearState): string {
	return normalizeKey(state.name || "");
}

function getStateIcon(state: LinearState): string | null {
	const key = getStateKey(state);

	switch (key) {
		case BoardColumnKey.Triage:
			return triageIcon;
		case BoardColumnKey.Backlog:
			return backlogIcon;
		case BoardColumnKey.Todo:
		case BoardColumnKey.ToDo:
			return todoIcon;
		case BoardColumnKey.InProgress:
			return inProgressIcon;
		case BoardColumnKey.InReview:
			return inReviewIcon;
		case BoardColumnKey.Done:
			return doneIcon;
		case BoardColumnKey.Cancelled:
		case BoardColumnKey.Canceled:
		case BoardColumnKey.Duplicate:
			return cancelledIcon;
		case BoardColumnKey.ExtendedFeatures:
			return extendedFeaturesIcon;
		case BoardColumnKey.PaperClip:
			return paperClipIcon;
		default:
			return null;
	}
}

export default function InternalBoardPage() {
	const { applicationId, issueId } = useParams();
	const navigate = useNavigate();
	const { instance } = useMsal();
	const scopes = useMemo(() => internalApiScopes, []);
	const { setLeftContent, setTitleContent } = useInternalHeader();

	const [title, setTitle] = useState<string>("");
	const [states, setStates] = useState<LinearState[]>([]);
	const [issuesByState, setIssuesByState] = useState<
		Record<string, LinearIssueSummary[]>
	>({});
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState<boolean>(true);

	const [createIssueOpen, setCreateIssueOpen] = useState<boolean>(false);
	const [createIssueTitle, setCreateIssueTitle] = useState<string>("");
	const [createIssueDescription, setCreateIssueDescription] =
		useState<string>("");
	const [createIssueBusy, setCreateIssueBusy] = useState<boolean>(false);
	const [createIssueError, setCreateIssueError] = useState<string | null>(null);

	const issueDrawerOpen = Boolean(issueId);

	const [recencyFilter, setRecencyFilter] = useState<string>("all");

	const reloadBoard = React.useCallback(async () => {
		if (!applicationId) return;
		setLoading(true);
		try {
			const res = await getBoard(instance, scopes, applicationId);
			setTitle(res.application?.name || "");
			setStates(res.states);
			setIssuesByState(res.issuesByState);
		} catch (e: any) {
			setError(e?.message || String(e));
		} finally {
			setLoading(false);
		}
	}, [applicationId, instance, scopes]);

	async function handleCreateIssueSubmission(e: React.FormEvent) {
		e.preventDefault();
		if (!applicationId) return;
		const title = createIssueTitle.trim();
		const description = createIssueDescription.trim();

		if (!title) {
			setCreateIssueError("Title is required");
			return;
		}
		setCreateIssueBusy(true);
		setCreateIssueError(null);

		try {
			await createIssue(instance, scopes, applicationId, {
				title,
				description: description ? description : undefined,
			});
			await reloadBoard();
			setCreateIssueOpen(false);
		} catch (err: any) {
			setCreateIssueError(err?.message || String(err));
		} finally {
			setCreateIssueBusy(false);
		}
	}
	const recencyFilteredIssuesByState = useMemo(() => {
		if (recencyFilter === "all") return issuesByState;

		const days = recencyFilter === "7d" ? 7 : 30;
		const cutoff = new Date();
		cutoff.setDate(cutoff.getDate() - days);

		const result: Record<string, LinearIssueSummary[]> = {};
		for (const [stateId, issues] of Object.entries(issuesByState)) {
			result[stateId] = issues.filter(
				(issues) => issues.updatedAt && new Date(issues.updatedAt) >= cutoff
			);
		}
		return result;
	}, [issuesByState, recencyFilter])

	useEffect(() => {
		reloadBoard();
	}, [reloadBoard]);

	useEffect(() => {
		const IDLE_DELAY = 30_000;
		const REFRESH_INTERVAL = 60_000;
		let idleTimer: ReturnType<typeof setTimeout>;
		let refreshTimer: ReturnType<typeof setInterval>;

		function startRefreshing() {
			stopRefreshing();
			reloadBoard();
			refreshTimer = setInterval(reloadBoard, REFRESH_INTERVAL);
		}

		function stopRefreshing() {
			clearInterval(refreshTimer);
		}

		function resetIdleTimer() {
			stopRefreshing();
			clearTimeout(idleTimer);
			idleTimer = setTimeout(startRefreshing, IDLE_DELAY);
		}

		const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
		events.forEach((e) => window.addEventListener(e, resetIdleTimer));
		idleTimer = setTimeout(startRefreshing, IDLE_DELAY);

		return () => {
			clearTimeout(idleTimer);
			clearInterval(refreshTimer);
			events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
		};
	}, [reloadBoard]);

	useEffect(() => {
		// setLeftContent(
		//   <Link to="/internal" aria-label="back to project overview" style={{ display: 'flex', alignItems: 'center' }}>
		//     <img className={styles.backArrow} src={backArrow} alt="" aria-hidden="true" style={{ marginRight: 0 }} />
		//   </Link>
		// );
		return () => setLeftContent(null);
	}, [setLeftContent]);

	useEffect(() => {
		setTitleContent(
			<div className={styles.title} style={{ fontSize: "14px" }}>
				{title}
			</div>,
		);
		return () => setTitleContent(null);
	}, [title, setTitleContent]);

	const totalIssues = Object.values(issuesByState).reduce(
		(acc, issues) => acc + issues.length,
		0,
	);

	return (
		<div
			className={`${styles.page} ${issueDrawerOpen ? styles.pageDrawerOpen : ""}`}
		>
			<InternalModal
				open={createIssueOpen}
				onClose={() => setCreateIssueOpen(false)}
				title="Create issue"
			>
				<form onSubmit={handleCreateIssueSubmission}>
					<div className={styles.issueTitleContainer}>
						<input
							type="text"
							className={styles.issueTitleText}
							value={createIssueTitle}
							onChange={(e) => setCreateIssueTitle(e.target.value)}
							placeholder="Issue Title"
							autoFocus={true}
							disabled={createIssueBusy}
						/>
					</div>
					<div className={styles.issueDescriptionContainer}>
						<textarea
							className={styles.issueDescriptionText}
							value={createIssueDescription}
							onChange={(e) => setCreateIssueDescription(e.target.value)}
							placeholder="Issue Description"
							disabled={createIssueBusy}
						/>
					</div>
					{createIssueError ? (
						<div className={styles.createIssueError}>{createIssueError}</div>
					) : null}
					<div className={styles.createIssueActions}>
						<button
							type="submit"
							className={styles.createIssuePrimary}
							disabled={createIssueBusy || !createIssueTitle.trim()}
						>
							{createIssueBusy ? "Creating…" : "Create"}
						</button>
					</div>
				</form>
			</InternalModal>
			{!createIssueOpen && !issueDrawerOpen && (
				<button
					className={styles.createIssue}
					onClick={() => {
						setCreateIssueOpen(true);
						setCreateIssueBusy(false);
						setCreateIssueError(null);
						setCreateIssueTitle("");
						setCreateIssueDescription("");
					}}
				>
					Create Issue
				</button>
			)}
			{error ? <div className={styles.error}>{error}</div> : null}

			{loading ? (
				<div className={styles.loadingContainer}>
					<div className={styles.spinner}></div>
				</div>
			) : totalIssues === 0 ? (
				<div className={styles.emptyState}>
					No issues found in this project.
				</div>
			) : (
				<div className={styles.boardContainer}>
					<div className={styles.filterOptionsContainer}>
						<button className={`${styles.filterOption} ${recencyFilter === "all" ? styles.filterOptionActive : ""}`} onClick={() => setRecencyFilter("all")}>All</button>
						<button className={`${styles.filterOption} ${recencyFilter === "7d" ? styles.filterOptionActive : ""}`} onClick={() => setRecencyFilter("7d")}>Updated last 7 days</button>
						<button className={`${styles.filterOption} ${recencyFilter === "30d" ? styles.filterOptionActive : ""}`} onClick={() => setRecencyFilter("30d")}>Updated last 30 days</button>
					</div>
					<div className={styles.board}>
						{states
							.filter((state) => (recencyFilteredIssuesByState[state.id] || []).length > 0)
							.slice()
							.sort((a, b) => {
								// Ordering rules:
								// 1) Triage (Linear state.type === "triage") always left-most
								// 2) States with a numeric position come next (ascending)
								// 3) States without a position appear at the end
								// 4) Canceled and Duplicate are always last
								const aIsCanceled = a.name === "Canceled";
								const bIsCanceled = b.name === "Canceled";
								if (aIsCanceled !== bIsCanceled) return aIsCanceled ? 1 : -1;

								const aIsDuplicate = a.name === "Duplicate";
								const bIsDuplicate = b.name === "Duplicate";
								if (aIsDuplicate !== bIsDuplicate) return aIsDuplicate ? 1 : -1;

								const aIsTriage = a.type === "triage";
								const bIsTriage = b.type === "triage";
								if (aIsTriage !== bIsTriage) return aIsTriage ? -1 : 1;

								const keyA = getStateKey(a);
								const keyB = getStateKey(b);
								if (
									keyA === BoardColumnKey.InReview &&
									keyB === BoardColumnKey.Done
								)
									return -1;
								if (
									keyA === BoardColumnKey.Done &&
									keyB === BoardColumnKey.InReview
								)
									return 1;

								const aHasPos = typeof a.position === "number";
								const bHasPos = typeof b.position === "number";
								if (aHasPos !== bHasPos) return aHasPos ? -1 : 1;

								if (!aHasPos && !bHasPos)
									return (a.name || "").localeCompare(b.name || "");

								return (a.position as number) - (b.position as number);
							})
							.map((state) => {
								const icon = getStateIcon(state);
								const stateKey = getStateKey(state);

								return (
									<div key={state.id} className={styles.column}>
										<div className={styles.columnHeader}>
											<div
												className={styles.columnTitle}
												data-state-key={stateKey}
											>
												{icon ? (
													<img
														className={styles.columnIcon}
														src={icon}
														alt=""
														aria-hidden="true"
													/>
												) : null}
												{state.name}
											</div>
											<div className={styles.columnCount}>
												{(issuesByState[state.id] || []).length}
											</div>
										</div>

										<div className={styles.columnBody}>
											{(issuesByState[state.id] || [])
												.slice()
												.sort((a, b) => {
													const tA = a.updatedAt
														? new Date(a.updatedAt).getTime()
														: 0;
													const tB = b.updatedAt
														? new Date(b.updatedAt).getTime()
														: 0;
													return tB - tA;
												})
												.map((issue) => (
													<div
														key={issue.id}
														className={`card ${styles.card}`}
														onClick={() =>
															navigate(
																`/internal/app/${applicationId}/issue/${issue.id}`,
																{ replace: true },
															)
														}
													>
														<div className={styles.cardTop}>
															<span className={styles.identifier}>
																{issue.identifier}
															</span>
														</div>
														<div className={styles.cardTitle}>{issue.title}</div>
													</div>
												))}
										</div>
									</div>
								);
							})}
					</div>
				</div>
			)}
			<Outlet />
		</div>
	);
}
